/**
 * First Glance: alternative range approximations (linear vs lognormal), shared time basis,
 * selectable central probability mass, and chain OI/volume-weighted strike targets.
 */

import type { ScannerData } from '../types/ScannerData';
import { normalizeScannerExpiryToIso } from './firstGlanceSpotGammaInsight';

/** Same shape as dashboard EffectiveRangeResult; kept local to avoid circular imports. */
export type RangeTimeSlice = {
  tYears: number;
  timeBasis: 'calendar_dte' | 'intraday_rth';
  sessionFractionRemaining: number | null;
};

export type FirstGlanceRangeMethod =
  | 'max_pain'
  | 'linear_1sigma'
  | 'linear_z'
  | 'lognormal_central'
  | 'blend_avg_linear_lognormal';

export type FirstGlanceRangeComputation = {
  expectedMove: number;
  rangeLow: number;
  rangeHigh: number;
  expectedMovePct: number;
  timeBasis: 'calendar_dte' | 'intraday_rth';
  sessionFractionRemaining: number | null;
  method: FirstGlanceRangeMethod;
  /** Central probability mass (two-sided) for z-based methods; null for fixed ±1σ linear. */
  centralMassPercent: number | null;
  /** z such that P(-z < Z < z) ≈ centralMass/100 for standard normal (method-dependent). */
  zUsed: number | null;
  labelShort: string;
};

const US_RTH_MINUTES = 390;
const MIN_SESSION_DAY_FRAC = 1 / US_RTH_MINUTES;

function getETHourMinute(d: Date): { h: number; m: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(d);
  const hv = parts.find((p) => p.type === 'hour')?.value ?? '0';
  const mv = parts.find((p) => p.type === 'minute')?.value ?? '0';
  return { h: parseInt(hv, 10) || 0, m: parseInt(mv, 10) || 0 };
}

export function isUSWeekdayET(d: Date): boolean {
  const wd = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
  }).format(d);
  return wd !== 'Sat' && wd !== 'Sun';
}

export function fractionRegularSessionRemainingET(now: Date = new Date()): number {
  if (!isUSWeekdayET(now)) return 1;
  const openM = 9 * 60 + 30;
  const closeM = 16 * 60;
  const { h, m } = getETHourMinute(now);
  const mins = h * 60 + m;
  if (mins < openM) return 1;
  if (mins >= closeM) return 0;
  return (closeM - mins) / US_RTH_MINUTES;
}

/**
 * Shared σ√T time slice: calendar DTE or 0DTE RTH fraction (same convention as legacy linear band).
 */
export function computeRangeTimeSlice(dte: number, refDate: Date = new Date()): RangeTimeSlice {
  let tYears: number;
  let timeBasis: RangeTimeSlice['timeBasis'] = 'calendar_dte';
  let sessionFractionRemaining: number | null = null;

  if (dte > 0) {
    tYears = dte / 365;
  } else {
    timeBasis = 'intraday_rth';
    const fRem = fractionRegularSessionRemainingET(refDate);
    sessionFractionRemaining = fRem;
    const dayFrac =
      fRem <= 0 ? MIN_SESSION_DAY_FRAC : fRem >= 1 ? 1 : Math.max(MIN_SESSION_DAY_FRAC, fRem);
    tYears = dayFrac / 252;
  }

  return { tYears, timeBasis, sessionFractionRemaining };
}

/**
 * z with P(-z < Z < z) = centralMass for Z ~ N(0,1), i.e. Φ(z) = 0.5 + centralMass/2.
 * centralMass in (0,1), e.g. 0.9 for 90% central.
 */
export function zForTwoSidedCentralMass(centralMass: number): number {
  const p = Math.min(0.9999, Math.max(0.0001, centralMass));
  const q = 0.5 + p / 2;
  return inverseNormalCDF(q);
}

/** Peter J. Acklam's inverse normal CDF approximation (public domain). */
function inverseNormalCDF(p: number): number {
  if (p <= 0 || p >= 1 || !Number.isFinite(p)) return 0;
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1,
    2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968,
    2.938163982698783,
  ];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];
  const plow = 0.02425;
  const phigh = 1 - plow;
  let q: number;
  let r: number;
  if (p < plow) {
    q = Math.sqrt(-2 * Math.log(p));
    const num = (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]);
    const den = ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    return num / den;
  }
  if (p > phigh) {
    q = Math.sqrt(-2 * Math.log(1 - p));
    const num = (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]);
    const den = ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    return -num / den;
  }
  q = p - 0.5;
  r = q * q;
  return (
    (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
    (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
  );
}

function buildResult(
  S: number,
  safePrice: number,
  rangeLow: number,
  rangeHigh: number,
  slice: RangeTimeSlice,
  method: FirstGlanceRangeMethod,
  centralMassPercent: number | null,
  zUsed: number | null,
  labelShort: string
): FirstGlanceRangeComputation {
  const expectedMove = Math.max(safePrice - rangeLow, rangeHigh - safePrice);
  const expectedMovePct = (expectedMove / safePrice) * 100;
  return {
    expectedMove,
    rangeLow,
    rangeHigh,
    expectedMovePct,
    timeBasis: slice.timeBasis,
    sessionFractionRemaining: slice.sessionFractionRemaining,
    method,
    centralMassPercent,
    zUsed,
    labelShort,
  };
}

/**
 * Full First Glance range: pick method and (for z-based methods) central mass in percent (50–99).
 */
export function computeFirstGlanceRangeModel(
  currentPrice: number,
  atmIvPercent: number,
  dte: number,
  refDate: Date,
  method: FirstGlanceRangeMethod,
  centralMassPercent: number
): FirstGlanceRangeComputation {
  if (method === 'max_pain') {
    throw new Error('max_pain range is computed via computeMaxPainRangeBand in firstGlanceMaxPain.ts');
  }
  const safePrice = Math.max(currentPrice, 1e-9);
  const ivPct = Number(atmIvPercent);
  const sigma = (Number.isFinite(ivPct) && ivPct > 0 ? ivPct : 0) / 100;
  const slice = computeRangeTimeSlice(dte, refDate);
  const sqrtT = Math.sqrt(Math.max(slice.tYears, 1e-12));
  const S = currentPrice;

  if (sigma <= 0 || !Number.isFinite(S) || S <= 0) {
    return buildResult(S, safePrice, S, S, slice, method, null, null, 'No IV');
  }

  if (method === 'linear_1sigma') {
    const em = safePrice * sigma * sqrtT;
    return buildResult(
      S,
      safePrice,
      S - em,
      S + em,
      slice,
      'linear_1sigma',
      null,
      1,
      'Linear ±1σ ATM (~68% if Gaussian returns)'
    );
  }

  const mass = Math.min(99, Math.max(50, centralMassPercent)) / 100;
  const z = zForTwoSidedCentralMass(mass);

  if (method === 'linear_z') {
    const em = safePrice * sigma * sqrtT * z;
    return buildResult(
      S,
      safePrice,
      S - em,
      S + em,
      slice,
      'linear_z',
      centralMassPercent,
      z,
      `Linear ±${z.toFixed(2)}σ (${centralMassPercent}% target mass)`
    );
  }

  if (method === 'lognormal_central') {
    const half = z * sigma * sqrtT;
    const low = S * Math.exp(-half);
    const high = S * Math.exp(half);
    return buildResult(
      S,
      safePrice,
      low,
      high,
      slice,
      'lognormal_central',
      centralMassPercent,
      z,
      `Lognormal central ${centralMassPercent}%`
    );
  }

  // blend_avg_linear_lognormal
  const emLin = safePrice * sigma * sqrtT * z;
  const halfLog = z * sigma * sqrtT;
  const lowL = S - emLin;
  const highL = S + emLin;
  const lowG = S * Math.exp(-halfLog);
  const highG = S * Math.exp(halfLog);
  const low = (lowL + lowG) / 2;
  const high = (highL + highG) / 2;
  return buildResult(
    S,
    safePrice,
    low,
    high,
    slice,
    'blend_avg_linear_lognormal',
    centralMassPercent,
    z,
    `Avg(linear ±z / lognormal) ${centralMassPercent}%`
  );
}

export type ChainAverageTargets = {
  oiWeightedStrike: number | null;
  volumeWeightedStrike: number | null;
  combinedAverageStrike: number | null;
  rowCount: number;
  totalOi: number;
  totalVol: number;
};

/**
 * OI- and volume-weighted average strike for the selected expiration (optional symbol filter).
 */
export function computeChainAverageTargets(
  rows: ScannerData[] | undefined,
  expiryIso: string,
  symbolUpper: string
): ChainAverageTargets {
  const empty: ChainAverageTargets = {
    oiWeightedStrike: null,
    volumeWeightedStrike: null,
    combinedAverageStrike: null,
    rowCount: 0,
    totalOi: 0,
    totalVol: 0,
  };
  if (!rows?.length || !expiryIso.trim()) return empty;

  const sym = (symbolUpper || '').trim().toUpperCase();
  let sumOiK = 0;
  let sumVolK = 0;
  let totalOi = 0;
  let totalVol = 0;
  let n = 0;

  for (const r of rows) {
    const rawExp = String(r['Expiry Date'] || '').trim();
    if (!rawExp || normalizeScannerExpiryToIso(rawExp) !== expiryIso) continue;
    if (sym) {
      const u = String(r.Underlying || r.underlyingSymbol || '').trim().toUpperCase();
      if (u && u !== sym) continue;
    }
    const k = Number(r.Strike);
    if (!Number.isFinite(k)) continue;
    const oi = Math.max(0, Number(r['Open Interest']) || 0);
    const vol = Math.max(0, Number(r.Volume) || 0);
    sumOiK += k * oi;
    sumVolK += k * vol;
    totalOi += oi;
    totalVol += vol;
    n += 1;
  }

  const oiWeightedStrike = totalOi > 1e-9 ? sumOiK / totalOi : null;
  const volumeWeightedStrike = totalVol > 1e-9 ? sumVolK / totalVol : null;
  let combinedAverageStrike: number | null = null;
  if (oiWeightedStrike != null && volumeWeightedStrike != null) {
    combinedAverageStrike = (oiWeightedStrike + volumeWeightedStrike) / 2;
  } else if (oiWeightedStrike != null) {
    combinedAverageStrike = oiWeightedStrike;
  } else if (volumeWeightedStrike != null) {
    combinedAverageStrike = volumeWeightedStrike;
  }

  return {
    oiWeightedStrike,
    volumeWeightedStrike,
    combinedAverageStrike,
    rowCount: n,
    totalOi,
    totalVol,
  };
}

/** UI presets for central two-sided mass (percent). */
export const FIRST_GLANCE_CENTRAL_PERCENT_OPTIONS = [50, 60, 68, 80, 90, 95, 99] as const;

export const FIRST_GLANCE_RANGE_METHOD_LABELS: Record<FirstGlanceRangeMethod, string> = {
  max_pain: 'Max pain (OI) — pin band narrows into the close',
  linear_1sigma: 'Linear ±1σ ATM',
  linear_z: 'Linear ±zσ (pick %)',
  lognormal_central: 'Lognormal central band',
  blend_avg_linear_lognormal: 'Average: linear-z & lognormal',
};
